export interface AgentTemplate {
  id: string;
  name: string;
  role: string;
  category: string;
  bestFor: string;
  tasks: string[];
  setupTime: string;
  automationLevel: number; // e.g. 95%
  humanApprovalRequired: boolean;
  status: 'Ready to Deploy' | 'Requires Connection' | 'Approval Recommended' | 'Admin Setup Required' | 'Premium Template';
  description: string;
  toolsConnected: string[];
  knowledgeSources: string[];
  exampleOutput: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  recommendedPlan: string;
  timeSaved: string; // value e.g. "12 Hours / Week"
  isFeatured?: boolean;
}

export interface TaskFlow {
  id: string;
  title: string;
  trigger: string;
  agentAssigned: string;
  steps: string[];
  approvalGate: string;
  output: string;
  estimatedTimeSaved: string;
}

export const AGENT_CATEGORIES = [
  "Executive & Admin",
  "Customer Support",
  "Sales & Lead Generation",
  "Product & Market Research",
  "Marketing & Content",
  "Social Media",
  "E-commerce",
  "Finance & Billing Support",
  "HR & Client Resource Workflow",
  "Operations",
  "Legal & Compliance Support",
  "Website & SEO",
  "Client Management",
  "Data & Reporting",
  "Personal Productivity"
];

export const READY_MADE_TEMPLATES: AgentTemplate[] = [
  {
    id: "exec-assistant",
    name: "Executive Assistant Agent",
    role: "Operational Scribe & Liaison",
    category: "Executive & Admin",
    bestFor: "Calendar coordination, daily agendas, and meeting notes summaries",
    tasks: [
      "Summarise emails",
      "Prepare meeting agenda",
      "Draft follow-up messages",
      "Create daily priority list",
      "Schedule reminders",
      "Prepare executive briefing",
      "Track open decisions",
      "Escalate urgent items"
    ],
    setupTime: "3 Minutes",
    automationLevel: 92,
    humanApprovalRequired: true,
    status: "Ready to Deploy",
    description: "Helps with scheduling, reminders, inbox summaries, meeting notes, follow-ups, task planning, and daily briefings on autopilot.",
    toolsConnected: ["Google Workspace", "Outlook", "Slack"],
    knowledgeSources: ["NexoraX Office SOP", "Director's Priority Mandates"],
    exampleOutput: "“Operational summary completed: 4 critical board responses drafted for Lead Director. Next step scheduling in 15 minutes.”",
    riskLevel: "LOW",
    recommendedPlan: "Corporate Alpha",
    timeSaved: "14 Hours / Week",
    isFeatured: true
  },
  {
    id: "cust-support",
    name: "Customer Support Assistant Agent",
    role: "L1 Support Triage & Draft Compiler",
    category: "Customer Support",
    bestFor: "Resolving common FAQs, ticket categorisation, and response drafts",
    tasks: [
      "Draft support replies",
      "Categorise tickets",
      "Detect urgent cases",
      "Suggest help articles",
      "Escalate complaints",
      "Track customer sentiment",
      "Create support summaries",
      "Update ticket status"
    ],
    setupTime: "5 Minutes",
    automationLevel: 88,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Handles customer questions, FAQs, ticket summaries, support drafts, and escalation routing securely.",
    toolsConnected: ["Zendesk", "Intercom", "Gmail"],
    knowledgeSources: ["Customer support FAQ bank", "Service Refund Standard Policy"],
    exampleOutput: "“Reply draft to ticket #4912 ready. Checked for SLA metrics. Standard FAQ answer applied for refund query.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "20 Hours / Week",
    isFeatured: true
  },
  {
    id: "sales-assistant",
    name: "Sales Assistant Agent",
    role: "Lead Nurture & Core CRM Sync",
    category: "Sales & Lead Generation",
    bestFor: "Qualifying leads, compiling proposal drafts, and setting database actions",
    tasks: [
      "Qualify leads",
      "Draft sales emails",
      "Create proposal outline",
      "Update CRM",
      "Schedule follow-up",
      "Summarise lead intent",
      "Suggest next action",
      "Prepare sales call notes"
    ],
    setupTime: "4 Minutes",
    automationLevel: 90,
    humanApprovalRequired: true,
    status: "Requires Connection",
    description: "Supports sales pipelines, lead follow-ups, proposals, and CRM updates recursively.",
    toolsConnected: ["HubSpot", "Salesforce", "Stripe"],
    knowledgeSources: ["NexoraX Sales Guidebook v3", "Proposal Pricing Formulas"],
    exampleOutput: "“High-value leadqualified: [BUSINESS_NAME] triggers the Resource resource query. Next step outbound email drafted.”",
    riskLevel: "MEDIUM",
    recommendedPlan: "Enterprise Pro",
    timeSaved: "15 Hours / Week",
    isFeatured: true
  },
  {
    id: "product-search",
    name: "Product Search Agent",
    role: "E-Commerce Supplier Mapping Node",
    category: "Product & Market Research",
    bestFor: "Market supplier research, buying comparison reports, and reviews summary",
    tasks: [
      "Search products",
      "Compare features",
      "Compare prices",
      "Check supplier options",
      "Summarise reviews",
      "Create shortlist",
      "Recommend best option",
      "Prepare purchase brief"
    ],
    setupTime: "2 Minutes",
    automationLevel: 95,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Finds products, compares options, analyses suppliers, tracks prices, and prepares buying recommendations.",
    toolsConnected: ["Amazon API", "AliExpress Scraper", "Google Sheets"],
    knowledgeSources: ["Company procurement compliance limits"],
    exampleOutput: "“Compare list finalized: 5 cloud hosting providers analyzed. Hetzner bare-metal recommended as cheapest SLA fit.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "8 Hours / Week"
  },
  {
    id: "market-research",
    name: "Market Research Agent",
    role: "Competitor Intelligence Intelligence Drone",
    category: "Product & Market Research",
    bestFor: "Competitor swot mapping, pricing analysis, and monthly market reports",
    tasks: [
      "Competitor analysis",
      "Market trend summary",
      "Customer pain point research",
      "SWOT analysis",
      "Pricing research",
      "Opportunity mapping",
      "Industry report draft",
      "Executive research summary"
    ],
    setupTime: "6 Minutes",
    automationLevel: 94,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Researches competitors, trends, customers, industries, and opportunities continuously.",
    toolsConnected: ["Google Search API", "Reddit Scraper", "Wikipedia API"],
    knowledgeSources: ["NexoraX Growth Objectives 2026"],
    exampleOutput: "“Intel report: 3 competing automation agencies launched pricing updates. Standard response compiled for marketing.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "18 Hours / Week",
    isFeatured: true
  },
  {
    id: "lead-qualification",
    name: "Lead Qualification Agent",
    role: "Inbound Sieve & Lead Scorer",
    category: "Sales & Lead Generation",
    bestFor: "Identifying budget signals and ranking high-intent buyers",
    tasks: [
      "Score lead quality",
      "Identify budget signals",
      "Identify urgency",
      "Categorise industry",
      "Suggest response",
      "Add CRM note",
      "Assign sales priority",
      "Trigger follow-up workflow"
    ],
    setupTime: "2 Minutes",
    automationLevel: 96,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Reviews inbound leads, scores them, and flags urgent budget triggers instantly.",
    toolsConnected: ["Typeform", "HubSpot", "ActiveCampaign"],
    knowledgeSources: ["BANT Qualifying Matrix Rules"],
    exampleOutput: "“Lead scored: 98/100 points. Budget verified above £50,000 threshold. Escalated to core director.”",
    riskLevel: "LOW",
    recommendedPlan: "Corporate Alpha",
    timeSaved: "12 Hours / Week"
  },
  {
    id: "email-management",
    name: "Email Management Agent",
    role: "Inbox Triage Officer",
    category: "Executive & Admin",
    bestFor: "Email filtering, drafting secure replies, and daily inbox briefings",
    tasks: [
      "Summarise inbox",
      "Flag urgent emails",
      "Draft replies",
      "Categorise emails",
      "Identify action items",
      "Create follow-up list",
      "Detect missed responses",
      "Prepare daily email briefing"
    ],
    setupTime: "4 Minutes",
    automationLevel: 85,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Manages email triage, drafts replies, and detects missed response windows securely.",
    toolsConnected: ["Gmail", "Outlook IMAP", "Slack Notify"],
    knowledgeSources: ["Primary Communications Anchors"],
    exampleOutput: "“Inbox briefing complete: 14 newsletters archived, 3 client escalations flagged, 3 drafts ready for review.”",
    riskLevel: "MEDIUM",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "22 Hours / Week"
  },
  {
    id: "calendar-scheduling",
    name: "Calendar & Scheduling Agent",
    role: "Availability Coordinator",
    category: "Executive & Admin",
    bestFor: "Meeting setups, conflict resolution, and booking notifications",
    tasks: [
      "Suggest meeting times",
      "Prepare meeting agenda",
      "Send scheduling draft",
      "Remind attendees",
      "Track deadlines",
      "Detect conflicts",
      "Create calendar summary",
      "Prepare next-day schedule"
    ],
    setupTime: "3 Minutes",
    automationLevel: 90,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Handles meetings, schedule summaries, reminders, and availability on autopilot.",
    toolsConnected: ["Google Calendar", "Outlook Calendar", "Cal.com"],
    knowledgeSources: ["Director's buffer slots & timezone guidelines"],
    exampleOutput: "“Booking request processed for Eleanor: Confirmed Thursday 3:00 PM GMT. Zoom link generated.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "10 Hours / Week"
  },
  {
    id: "content-writer",
    name: "Content Writer Agent",
    role: "Copywriting Architect",
    category: "Marketing & Content",
    bestFor: "Landing page copies, SEO blog structures, and promotional callouts",
    tasks: [
      "Draft blog posts",
      "Create captions",
      "Write email campaigns",
      "Create landing page copy",
      "Rewrite content",
      "Generate CTA ideas",
      "Create content calendar",
      "Prepare SEO headings"
    ],
    setupTime: "5 Minutes",
    automationLevel: 80,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Creates premium website, blog, email, and high-impact social content securely.",
    toolsConnected: ["WordPress", "Webflow API", "Mailchimp"],
    knowledgeSources: ["NexoraX Tone & Brand Guidelines"],
    exampleOutput: "“Email marketing campaign draft compiled: Theme Focus: 'The Wednesday Liquidity Deadline' has been generated.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "16 Hours / Week"
  },
  {
    id: "social-media-mgr",
    name: "Social Media Manager Agent",
    role: "Multi-Platform Scheduler",
    category: "Social Media",
    bestFor: "Drafting caption packs, platform adaptation, and hashtag research",
    tasks: [
      "Create post ideas",
      "Generate captions",
      "Suggest hashtags",
      "Draft weekly content plan",
      "Prepare platform versions",
      "Track engagement",
      "Suggest best posting times",
      "Send to approval queue"
    ],
    setupTime: "5 Minutes",
    automationLevel: 82,
    humanApprovalRequired: true,
    status: "Premium Template",
    description: "Plans, formats, structures, and schedules multi-platform content on autopilot.",
    toolsConnected: ["LinkedIn API", "TikTok Biz Suite", "Instagram Graph"],
    knowledgeSources: ["NexoraX Ad Virality Guide 2026"],
    exampleOutput: "“Weekly LinkedIn post pack formatted: 5 updates verified under NexoraX brand guidelines.”",
    riskLevel: "MEDIUM",
    recommendedPlan: "Corporate Alpha",
    timeSaved: "15 Hours / Week"
  },
  {
    id: "video-script",
    name: "Video Script Agent",
    role: "Vertical UGC Scriptwriter",
    category: "Social Media",
    bestFor: "Generating hook formulas, caption scripts, and UGC scene boards",
    tasks: [
      "Generate video hooks",
      "Write 15-second script",
      "Write 30-second script",
      "Create voiceover text",
      "Create scene list",
      "Create captions",
      "Suggest thumbnail text",
      "Send to video generator"
    ],
    setupTime: "3 Minutes",
    automationLevel: 85,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Creates scripts for Reels, TikTok, YouTube Shorts, and promotional videos.",
    toolsConnected: ["Decent UGC generator", "OpenAI TTS", "ElevenLabs API"],
    knowledgeSources: ["Nexorax Ad Factory Script Models"],
    exampleOutput: "“UGC 15-second script completed: Hook: ‘Why 97% of hiring pipelines fail in 8 seconds’ generated.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "12 Hours / Week"
  },
  {
    id: "ugc-campaign",
    name: "UGC Campaign Agent",
    role: "A/B Concept Builder",
    category: "Social Media",
    bestFor: "Developing testimonial briefs and creator hook variations",
    tasks: [
      "Create UGC script",
      "Suggest creator angle",
      "Generate hook variations",
      "Create CTA options",
      "Build A/B variants",
      "Prepare platform captions",
      "Send to approval",
      "Track winning script"
    ],
    setupTime: "4 Minutes",
    automationLevel: 80,
    humanApprovalRequired: true,
    status: "Premium Template",
    description: "Creates testimonial-style and creator-style campaign concepts with variable testing.",
    toolsConnected: ["TikTok Creator Ads Portal", "Google Drive Vault"],
    knowledgeSources: ["NexoraX Conversion metrics"],
    exampleOutput: "“A/B test variations complete: 3 testimonial hooks compiled with direct CTA link insertion.”",
    riskLevel: "LOW",
    recommendedPlan: "Enterprise Pro",
    timeSaved: "14 Hours / Week"
  },
  {
    id: "seo-assistant",
    name: "SEO Assistant Agent",
    role: "Page Visibility Auditor",
    category: "Website & SEO",
    bestFor: "Meta descriptions, keywords suggestions, and heading tags checks",
    tasks: [
      "Generate meta titles",
      "Generate meta descriptions",
      "Suggest keywords",
      "Audit page copy",
      "Create blog ideas",
      "Improve headings",
      "Check internal links",
      "Create SEO action list"
    ],
    setupTime: "3 Minutes",
    automationLevel: 94,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Audit website page copy, generates keyword outlines, and optimizes web signals instantly.",
    toolsConnected: ["Google Search Console", "SEMrush API", "Sitemap Crawler"],
    knowledgeSources: ["NexoraX SEO Ranking Matrix"],
    exampleOutput: "“Meta titles & tags optimized. Keyword density adjusted to conform to target index rules.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "10 Hours / Week"
  },
  {
    id: "website-update",
    name: "Website Update Agent",
    role: "Changelog Watcher Node",
    category: "Website & SEO",
    bestFor: "Scanning page changes, creating draft release logs, and notifying admins",
    tasks: [
      "Scan changed pages",
      "Summarise update",
      "Suggest guide video",
      "Suggest promo video",
      "Create changelog",
      "Notify admin",
      "Create draft announcement",
      "Send to approval"
    ],
    setupTime: "4 Minutes",
    automationLevel: 90,
    humanApprovalRequired: true,
    status: "Admin Setup Required",
    description: "Detects website changes, generates elegant changelogs, and drafts announcements on the fly.",
    toolsConnected: ["NexoraX Watcher API", "GitHub API", "Slack Webhook"],
    knowledgeSources: ["NexoraX Version Guidelines"],
    exampleOutput: "“Core change detected: Sandbox telemetry updated to v12.7. Draft Release note dispatched to admin.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "8 Hours / Week"
  },
  {
    id: "blog-writer",
    name: "Blog Writer Agent",
    role: "Strategic Writer Node",
    category: "Marketing & Content",
    bestFor: "Deep dive blog blueprints, outlines, and social snippet creation",
    tasks: [
      "Generate blog outline",
      "Draft article",
      "Create SEO title",
      "Create summary",
      "Suggest images",
      "Create social snippets",
      "Add CTA",
      "Prepare publishing draft"
    ],
    setupTime: "5 Minutes",
    automationLevel: 80,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Creates full-length blog articles, outlines, key takeaways, and backlink tags.",
    toolsConnected: ["WordPress", "Webflow API", "Jasper AI API"],
    knowledgeSources: ["Standard content SEO manuals"],
    exampleOutput: "“Outline & Draft generated: ‘The Future of Decoupled NexoraX Systems Architecture’ is ready for review.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "16 Hours / Week"
  },
  {
    id: "newsletter",
    name: "Newsletter Agent",
    role: "Broadcast Dispatcher",
    category: "Marketing & Content",
    bestFor: "Creating newsletter drafts, A/B testing headline metrics, and schedules",
    tasks: [
      "Draft newsletter",
      "Create subject lines",
      "Create preview text",
      "Add CTA",
      "Segment audience",
      "Prepare send schedule",
      "Generate A/B variants",
      "Track performance"
    ],
    setupTime: "3 Minutes",
    automationLevel: 84,
    humanApprovalRequired: true,
    status: "Requires Connection",
    description: "Creates regular email newsletter campaigns, subject lines, and targets segments securely.",
    toolsConnected: ["Mailchimp", "Klaviyo", "NexoraX Mail Portal"],
    knowledgeSources: ["Audience Segments Data"],
    exampleOutput: "“Subject Line Variant A vs B configured. Draft template ready for Director authorization.”",
    riskLevel: "MEDIUM",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "10 Hours / Week"
  },
  {
    id: "proposal-writer",
    name: "Proposal Writer Agent",
    role: "Deal Architect Drone",
    category: "Sales & Lead Generation",
    bestFor: "SOW definitions, timeline charts, and pricing outline setups",
    tasks: [
      "Draft proposal",
      "Create scope of work",
      "Add deliverables",
      "Add timeline",
      "Add pricing placeholder",
      "Create executive summary",
      "Prepare client version",
      "Send to approval"
    ],
    setupTime: "4 Minutes",
    automationLevel: 88,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Creates high-conversion corporate business proposals and structured SOW guidelines.",
    toolsConnected: ["Google Docs", "PandaDoc", "NexoraX Task Matrix"],
    knowledgeSources: ["Corporate Pricing & Delivery SLA"],
    exampleOutput: "“Custom proposal for [BUSINESS_NAME] drafted: 18-Sector infrastructure rollout specified.”",
    riskLevel: "MEDIUM",
    recommendedPlan: "Enterprise Pro",
    timeSaved: "20 Hours / Week"
  },
  {
    id: "invoice-billing",
    name: "Invoice & Billing Assistant Agent",
    role: "Securing Remittance Liaison",
    category: "Finance & Billing Support",
    bestFor: "Compiling client-safe payment alerts, summaries of overdue invoices",
    tasks: [
      "Draft invoice reminders",
      "Summarise unpaid invoices",
      "Prepare billing notes",
      "Track payment status",
      "Flag overdue accounts",
      "Create client-safe message",
      "Send billing alert",
      "Escalate payment issue"
    ],
    setupTime: "5 Minutes",
    automationLevel: 90,
    humanApprovalRequired: true,
    status: "Admin Setup Required",
    description: "Supports billing administration without exposing details of direct banking configurations.",
    toolsConnected: ["QuickBooks", "Xero API", "Stripe API"],
    knowledgeSources: ["Corporate billing SLA guidelines", "Secure Payment Provider payment tracks"],
    exampleOutput: "“Overdue tracker warning compiled for executive notice: 3 invoices flagged. Friendly client reminders ready.”",
    riskLevel: "HIGH",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "12 Hours / Week",
    isFeatured: true
  },
  {
    id: "finance-summary",
    name: "Finance Summary Agent",
    role: "Accounting Intelligence Core",
    category: "Finance & Billing Support",
    bestFor: "Synthesizing revenue counters and preparing multi-stage tax estimates",
    tasks: [
      "Summarise revenue",
      "Summarise costs",
      "Create monthly report",
      "Flag anomalies",
      "Prepare forecast",
      "Create KPI summary",
      "Generate management note",
      "Require approval for claims"
    ],
    setupTime: "6 Minutes",
    automationLevel: 92,
    humanApprovalRequired: true,
    status: "Premium Template",
    description: "Creates safe high-fidelity finance summaries from pre-approved system inputs.",
    toolsConnected: ["Excel Online", "Xero", "Secure Payment Provider Meter"],
    knowledgeSources: ["NexoraX 1% Levy & 10% Maintenance & Hosting Fee logic"],
    exampleOutput: "“Monthly Ledger consolidated: Current estimated Net Settlement mapped. Tax reserves checked and allocated.”",
    riskLevel: "HIGH",
    recommendedPlan: "Corporate Alpha",
    timeSaved: "18 Hours / Week"
  },
  {
    id: "hr-Client Resource Workflow",
    name: "HR Client Resource Workflow Agent",
    role: "Applicant Scribe Node",
    category: "HR & Client Resource Workflow",
    bestFor: "Generating job description drafts, CV screenings, and questions packages",
    tasks: [
      "Draft job descriptions",
      "Screen CV summaries",
      "Create interview questions",
      "Score applicants",
      "Schedule interviews",
      "Draft candidate emails",
      "Prepare hiring summary",
      "Escalate shortlisted candidates"
    ],
    setupTime: "4 Minutes",
    automationLevel: 86,
    humanApprovalRequired: true,
    status: "Ready to Deploy",
    description: "Screen CV resumes, drafts job criteria, and scores candidate parameters on secure grids.",
    toolsConnected: ["Ashby API", "Workable", "Lever API"],
    knowledgeSources: ["Secure Invoice Register role parameters", "NexoraX operational hierarchy standards"],
    exampleOutput: "“Candidate CV screen complete: Checked 14 Private Secretary applicants. Helen identified as structural match.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "15 Hours / Week"
  },
  {
    id: "onboarding-agent",
    name: "Onboarding Agent",
    role: "Client Activation Facilitator",
    category: "Client Management",
    bestFor: "Coordinating onboarding lists, collecting brand details, and warning logs",
    tasks: [
      "Create onboarding checklist",
      "Send welcome message",
      "Collect brand details",
      "Guide account setup",
      "Track completion",
      "Send reminders",
      "Prepare onboarding report",
      "Escalate incomplete steps"
    ],
    setupTime: "3 Minutes",
    automationLevel: 91,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Guides new clients or incoming staff through account setup and asset collection workflows.",
    toolsConnected: ["Typeform API", "HubSpot CRM", "Google Drive"],
    knowledgeSources: ["Onboarding Welcome SOP Guide"],
    exampleOutput: "“Onboarding sequence initialized for [BUSINESS_NAME]. Welcome message dispatched securely.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "10 Hours / Week"
  },
  {
    id: "training-agent",
    name: "Training Agent",
    role: "Internal SOP Compiler",
    category: "HR & Client Resource Workflow",
    bestFor: "Generating lesson outlines, process summaries, and learning quizzes",
    tasks: [
      "Generate training plan",
      "Create lesson outline",
      "Create quiz questions",
      "Summarise process",
      "Create SOP draft",
      "Track progress",
      "Recommend next module",
      "Create completion report"
    ],
    setupTime: "4 Minutes",
    automationLevel: 88,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Creates micro-training blocks, interactive process quizzes, and training compliance lists.",
    toolsConnected: ["LMS API", "Google Docs Suite", "Notion API"],
    knowledgeSources: ["Company operational guidelines", "Core security protocols"],
    exampleOutput: "“Module outline ready: ‘Node-12.7 Operational NexoraXty Guide’ published for training tracking.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "8 Hours / Week"
  },
  {
    id: "operations-assistant",
    name: "Operations Assistant Agent",
    role: "Activity Scribe & Task Router",
    category: "Operations",
    bestFor: "Schedules tracking, owner assignments, and daily delay reports",
    tasks: [
      "Create task list",
      "Assign owner",
      "Track status",
      "Flag delays",
      "Summarise operations",
      "Prepare daily report",
      "Escalate blockers",
      "Recommend workflow improvement"
    ],
    setupTime: "4 Minutes",
    automationLevel: 90,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Coordinates operational tasks, monitors delay logs, and drafts weekly productivity reports.",
    toolsConnected: ["Asana API", "Jira Software", "NexoraX Task Matrix"],
    knowledgeSources: ["Sponsorship License SOP milestones"],
    exampleOutput: "“Operations update: Task checklist checked. Staff Coordinator marked as 'Pending-Migration' - monitoring.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "14 Hours / Week"
  },
  {
    id: "project-manager",
    name: "Project Manager Agent",
    role: "Milestone Tracking Engine",
    category: "Operations",
    bestFor: "Tracking milestone blocks, preparing status reports, and project charts",
    tasks: [
      "Create project plan",
      "Track milestones",
      "Summarise progress",
      "Identify risks",
      "Assign next actions",
      "Draft client update",
      "Prepare status report",
      "Escalate delays"
    ],
    setupTime: "5 Minutes",
    automationLevel: 85,
    humanApprovalRequired: true,
    status: "Ready to Deploy",
    description: "Supports milestone tracking, compiles daily progress summaries, and issues risk reports.",
    toolsConnected: ["ClickUp", "Trello API", "Slack Notify"],
    knowledgeSources: ["Internal delivery handbook v1"],
    exampleOutput: "“Milestone achieved: ‘Sandbox Grid Deployment v2’ complete. Status report prepared for review.”",
    riskLevel: "LOW",
    recommendedPlan: "Enterprise Pro",
    timeSaved: "18 Hours / Week"
  },
  {
    id: "crm-update",
    name: "CRM Update Agent",
    role: "Database Synchronization Scribe",
    category: "Client Management",
    bestFor: "Consolidating call logs, assigning deals, and flagging outdated contacts",
    tasks: [
      "Summarise client interaction",
      "Add CRM note",
      "Update lead status",
      "Create follow-up task",
      "Flag missing details",
      "Detect stale leads",
      "Suggest next action",
      "Sync approved updates"
    ],
    setupTime: "3 Minutes",
    automationLevel: 94,
    humanApprovalRequired: true,
    status: "Requires Connection",
    description: "Syncs customer interaction notes and assigns scheduling priorities directly in CRMs.",
    toolsConnected: ["Salesforce", "Pipedrive API", "HubSpot CRM"],
    knowledgeSources: ["NexoraX Database standards"],
    exampleOutput: "“Note appended: Client meeting summaries drafted for [BUSINESS_NAME]. Task set for next Tuesday.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "12 Hours / Week"
  },
  {
    id: "client-success",
    name: "Client Success Agent",
    role: "Retention Alert Drone",
    category: "Client Management",
    bestFor: "Churn signals detection, automated reviews check, and check-in drafts",
    tasks: [
      "Track client activity",
      "Detect churn risk",
      "Create check-in message",
      "Summarise results",
      "Suggest upsell",
      "Prepare review note",
      "Track satisfaction",
      "Escalate unhappy clients"
    ],
    setupTime: "4 Minutes",
    automationLevel: 88,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Identifies system drop-off rates, compiles satisfaction ratings, and flags churn alerts.",
    toolsConnected: ["Segment API", "Mixpanel Engine", "Zendesk Support"],
    knowledgeSources: ["NexoraX retention rulebooks"],
    exampleOutput: "“Client check-in required: Account [BUSINESS_NAME] inactivity detected. Check-in email draft ready.”",
    riskLevel: "MEDIUM",
    recommendedPlan: "Enterprise Pro",
    timeSaved: "11 Hours / Week"
  },
  {
    id: "review-reputation",
    name: "Review & Reputation Agent",
    role: "Testimonials Harvester Node",
    category: "Client Management",
    bestFor: "Drafting reviews invitations, scanning sentiments, and sentiment summaries",
    tasks: [
      "Draft review request",
      "Monitor review sentiment",
      "Summarise feedback",
      "Create testimonial draft",
      "Suggest response",
      "Flag negative review",
      "Prepare public reply",
      "Send to approval"
    ],
    setupTime: "2 Minutes",
    automationLevel: 92,
    humanApprovalRequired: true,
    status: "Ready to Deploy",
    description: "Identifies positive transactions to trigger review requests and drafts responses.",
    toolsConnected: ["Google Business Profile API", "Trustpilot API", "Yext Platform"],
    knowledgeSources: ["Corporate PR manual"],
    exampleOutput: "“Review detected: Five-star rating registered. Public answer compiled for Director authorization.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "7 Hours / Week"
  },
  {
    id: "ecommerce-product",
    name: "E-commerce Product Agent",
    role: "Listing Optimization Wizard",
    category: "E-commerce",
    bestFor: "Compiling SEO listings, bulleted features catalogs, and listings updates",
    tasks: [
      "Create product description",
      "Generate product title",
      "Create feature bullets",
      "Suggest pricing note",
      "Write SEO copy",
      "Create product FAQ",
      "Generate ad copy",
      "Prepare listing update"
    ],
    setupTime: "3 Minutes",
    automationLevel: 95,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Produces conversion-optimized e-commerce listings, FAQs, and product details on autopilot.",
    toolsConnected: ["Shopify Admin API", "WooCommerce API", "NexoraX Drive"],
    knowledgeSources: ["E-commerce styling directives and tone parameters"],
    exampleOutput: "“Listing updated: Shopify payload with 5 bulleted features ready for catalog sync.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "14 Hours / Week"
  },
  {
    id: "supplier-research",
    name: "Supplier Research Agent",
    role: "Procurement Sieve Node",
    category: "E-commerce",
    bestFor: "Filtering suppliers, price sheets analysis, and supplier risk reports",
    tasks: [
      "Search suppliers",
      "Compare pricing",
      "Compare delivery",
      "Check reviews",
      "Create shortlist",
      "Prepare supplier summary",
      "Flag risks",
      "Recommend best option"
    ],
    setupTime: "3 Minutes",
    automationLevel: 90,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Researches supply chains, compiles cost sheets, and points out logistical risks.",
    toolsConnected: ["ThomasNet Supplier API", "Alibaba scraper", "Google Sheets"],
    knowledgeSources: ["Logistics and border parameters"],
    exampleOutput: "“Supply research completed: 4 micro-factories vetted for high-velocity compute shipments.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "9 Hours / Week"
  },
  {
    id: "data-reporting",
    name: "Data Reporting Agent",
    role: "KPI Scribe drone",
    category: "Data & Reporting",
    bestFor: "Creating charts, drafting management briefs, and anomaly logs alerts",
    tasks: [
      "Summarise data",
      "Generate KPI report",
      "Detect trends",
      "Flag anomalies",
      "Create chart summary",
      "Prepare executive report",
      "Recommend action",
      "Export report draft"
    ],
    setupTime: "4 Minutes",
    automationLevel: 96,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Analyzes system tracking files and outputs clean reports and summaries for decision-making.",
    toolsConnected: ["PowerBI API", "Google BigQuery", "Recharts Builder"],
    knowledgeSources: ["Metric definition standards"],
    exampleOutput: "“Weekly KPI dashboard consolidated: Active CPC rate sits optimal at £0.38.”",
    riskLevel: "LOW",
    recommendedPlan: "Corporate Alpha",
    timeSaved: "15 Hours / Week"
  },
  {
    id: "legal-draft-support",
    name: "Legal Drafting Support Agent",
    role: "Compliance Outline Drone",
    category: "Legal & Compliance Support",
    bestFor: "Policy outlines compilation, audit briefs, and terms of service analysis",
    tasks: [
      "Draft policy outline",
      "Summarise terms",
      "Create compliance checklist",
      "Flag risky language",
      "Prepare review notes",
      "Create client-safe draft",
      "Require human approval",
      "Escalate legal-sensitive content"
    ],
    setupTime: "6 Minutes",
    automationLevel: 80,
    humanApprovalRequired: true,
    status: "Premium Template",
    description: "Creates non-legal administrative drafts and compliance guidelines. Human review mandatory.",
    toolsConnected: ["NexoraX Drive", "LexisNexis API", "Word Template Builder"],
    knowledgeSources: ["Sponsorship Compliance guidelines SC887095"],
    exampleOutput: "“Sponsorship compliance checklist drafted. Core parameters matching Home Office requirements verified.”",
    riskLevel: "HIGH",
    recommendedPlan: "Corporate Alpha",
    timeSaved: "10 Hours / Week"
  },
  {
    id: "compliance-assistant",
    name: "Compliance Assistant Agent",
    role: "Audit Safeguard Coordinator",
    category: "Legal & Compliance Support",
    bestFor: "Verification checklists, records trails, and tracking policy updates",
    tasks: [
      "Create compliance checklist",
      "Track policy updates",
      "Summarise audit notes",
      "Flag missing records",
      "Prepare review summary",
      "Record approval trail",
      "Notify admin",
      "Escalate risk"
    ],
    setupTime: "5 Minutes",
    automationLevel: 88,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Maintains legal checklists, logs approval steps, and tracks compliance requirements.",
    toolsConnected: ["Gavel Compliance Engine", "SharePoint API"],
    knowledgeSources: ["Legal framework standards of NexoraX Digital"],
    exampleOutput: "“Internal billing audit logs mirrored. Double signature trails registered securely.”",
    riskLevel: "HIGH",
    recommendedPlan: "Corporate Alpha",
    timeSaved: "12 Hours / Week"
  },
  {
    id: "meeting-notes",
    name: "Meeting Notes Agent",
    role: "Minutes Scribe Node",
    category: "Operations",
    bestFor: "Transcription summarize, task allocation lists, and follow-ups scheduling",
    tasks: [
      "Summarise meeting notes",
      "Extract decisions",
      "Create action items",
      "Assign owners",
      "Set deadlines",
      "Draft follow-up email",
      "Create meeting archive",
      "Send approval summary"
    ],
    setupTime: "2 Minutes",
    automationLevel: 94,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Takes transcription text files and turns them into clean, formatted action plans with allocated tasks.",
    toolsConnected: ["Zoom API", "Calendar API", "NexoraX Task Matrix"],
    knowledgeSources: ["Operational team lists"],
    exampleOutput: "“Board briefing transcripts processed: 4 assignments logged for Staff Coordinator. Next actions defined.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "8 Hours / Week"
  },
  {
    id: "document-assistant",
    name: "Document Assistant Agent",
    role: "Draft Editor Drone",
    category: "Operations",
    bestFor: "Document summarization, outline compiling, and draft updates comparison",
    tasks: [
      "Summarise document",
      "Extract key points",
      "Rewrite text",
      "Create document outline",
      "Generate PDF draft",
      "Create checklist",
      "Compare document versions",
      "Flag missing sections"
    ],
    setupTime: "2 Minutes",
    automationLevel: 92,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Summarizes documents, extracts key arguments, and prepares clean outlines and structures.",
    toolsConnected: ["NexoraX Drive", "Acrobat API", "DocuSign Helper"],
    knowledgeSources: ["Formatting styling standard guides"],
    exampleOutput: "“PDF outline complete: 48-page corporate document summarized down to 4 critical pages.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "10 Hours / Week"
  },
  {
    id: "translation-agent",
    name: "Translation & Localisation Agent",
    role: "Cross-Market Adapter",
    category: "Marketing & Content",
    bestFor: "Multilingual translation, tone localisation, and local compliance checks",
    tasks: [
      "Translate content",
      "Localise tone",
      "Adapt CTA",
      "Create multilingual captions",
      "Check cultural fit",
      "Prepare local version",
      "Generate language variants",
      "Send to approval"
    ],
    setupTime: "3 Minutes",
    automationLevel: 90,
    humanApprovalRequired: true,
    status: "Ready to Deploy",
    description: "Adapts advertising copy and website copy for various language markets securely.",
    toolsConnected: ["Deepl Translation API", "Google Translate Enterprise"],
    knowledgeSources: ["NexoraX Regional Glossaries"],
    exampleOutput: "“Copy localiesd to Arabic: High-velocity callouts formatted to appeal to Middle Eastern enterprise.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "11 Hours / Week"
  },
  {
    id: "voice-assistant-script",
    name: "Voice Assistant Script Agent",
    role: "Liaison Script Compiler",
    category: "Customer Support",
    bestFor: "Dial-in greeting scripts, call flow charts, and ticket routing rules",
    tasks: [
      "Write greeting script",
      "Create FAQ responses",
      "Create call flow",
      "Draft escalation language",
      "Create appointment script",
      "Generate voice tone variants",
      "Create closing CTA",
      "Prepare test script"
    ],
    setupTime: "4 Minutes",
    automationLevel: 85,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Creates greeting scripts, FAQ guides, and call routes for conversational voice systems.",
    toolsConnected: ["NexoraX Voice API", "Twilio SIP", "ElevenLabs Voice"],
    knowledgeSources: ["Liaison support guidebooks v2"],
    exampleOutput: "“Greeting script generated: Optimized 0.8s response buffer to initiate customer calls.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "12 Hours / Week"
  },
  {
    id: "personal-prod",
    name: "Personal Productivity Agent",
    role: "Focus Blocks Scheduler",
    category: "Personal Productivity",
    bestFor: "Task prioritisation, reminder alerts, and custom weekly review sheets",
    tasks: [
      "Create daily plan",
      "Prioritise tasks",
      "Summarise reminders",
      "Track deadlines",
      "Create focus blocks",
      "Draft messages",
      "Prepare weekly review",
      "Suggest productivity improvements"
    ],
    setupTime: "2 Minutes",
    automationLevel: 95,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Synthesizes checklists and allocates focus blocks to keep individuals on target.",
    toolsConnected: ["Google Tasks", "Todoist API", "Notion Pages"],
    knowledgeSources: ["Time-blocking guidelines and Eisenhower matrix rules"],
    exampleOutput: "“Daily schedule streamlined: Space allocated for 3 deep-focus blocks on infrastructure compile.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "6 Hours / Week"
  },
  {
    id: "ai-research",
    name: "AI Research Assistant Agent",
    role: "Research Core Scribe",
    category: "Product & Market Research",
    bestFor: "Deep theme research, pros/cons sheets, and summary briefing briefs",
    tasks: [
      "Research topic",
      "Summarise findings",
      "Compare options",
      "Create source notes",
      "Draft briefing",
      "Prepare pros and cons",
      "Suggest next research",
      "Send review draft"
    ],
    setupTime: "4 Minutes",
    automationLevel: 92,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Spins custom deep research queries on topic guidelines and outputs reports.",
    toolsConnected: ["Google Scholar Crawler", "Arxiv API Hub"],
    knowledgeSources: ["Subject boundaries manual"],
    exampleOutput: "“Research briefing completed: 15 quantum billing schemas analyzed. Ledger compliance validated.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "14 Hours / Week"
  },
  {
    id: "website-chat",
    name: "Website Chat Agent",
    role: "Live Intake Coordinator",
    category: "Customer Support",
    bestFor: "Visitor greeting, capturing leads, and booking session routing",
    tasks: [
      "Answer FAQs",
      "Recommend services",
      "Capture lead details",
      "Route support request",
      "Suggest booking",
      "Send transcript",
      "Score lead intent",
      "Escalate complex query"
    ],
    setupTime: "3 Minutes",
    automationLevel: 86,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Acts as chat intake point to screen users and qualify sales inquiries instantly.",
    toolsConnected: ["LiveChat Widget API", "HubSpot CRM"],
    knowledgeSources: ["FAQ and company service details list"],
    exampleOutput: "“Lead captured: Visitor from [BUSINESS_NAME] seeking custom Resource deployment setups. CRM synced.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "15 Hours / Week"
  },
  {
    id: "booking-assistant",
    name: "Booking Assistant Agent",
    role: "Meeting Schedulers Drone",
    category: "Client Management",
    bestFor: "Coordinating meeting timings, booking slots updates, and reminders",
    tasks: [
      "Collect booking request",
      "Suggest availability",
      "Confirm meeting details",
      "Send reminder",
      "Prepare agenda",
      "Update calendar",
      "Notify team",
      "Follow up after meeting"
    ],
    setupTime: "3 Minutes",
    automationLevel: 92,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Examines calendar availability tables to set bookings and trigger reminders.",
    toolsConnected: ["Cal.com", "Google Calendar", "Outlook Calendar"],
    knowledgeSources: ["Staff focus schedules and block lists"],
    exampleOutput: "“Meeting locked: Demo meeting confirmed for Wednesday morning. Notification dispatched.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "10 Hours / Week"
  },
  {
    id: "service-recommendation",
    name: "Service Recommendation Agent",
    role: "Algorithmic Discovery Node",
    category: "Sales & Lead Generation",
    bestFor: "Analysing client answers, matching plans, and quote creations",
    tasks: [
      "Ask discovery questions",
      "Identify business need",
      "Match service",
      "Explain benefits",
      "Suggest plan",
      "Create quote request",
      "Route to sales",
      "Prepare recommendation summary"
    ],
    setupTime: "2 Minutes",
    automationLevel: 94,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Runs discover questions to point customers to the best NexoraX operational solution.",
    toolsConnected: ["NexoraX Pricing Database", "Hubspot CRM"],
    knowledgeSources: ["Company service maps and pricing metrics"],
    exampleOutput: "“Service matched: Level-3 NexoraX Vault selected as ideal fit for [BUSINESS_NAME].”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "8 Hours / Week"
  },
  {
    id: "automation-ideas",
    name: "Automation Ideas Agent",
    role: "Workflow Auditor Node",
    category: "Operations",
    bestFor: "Inspecting task sheets, estimating time-saving benchmarks, and roadmap creation",
    tasks: [
      "Analyse manual tasks",
      "Identify repetitive work",
      "Estimate time savings",
      "Suggest automation",
      "Prioritise workflows",
      "Create roadmap",
      "Recommend agent template",
      "Send to plan approval"
    ],
    setupTime: "4 Minutes",
    automationLevel: 88,
    humanApprovalRequired: true,
    status: "Ready to Deploy",
    description: "Audit manual tasks descriptions and outputs custom templates and steps recommendations.",
    toolsConnected: ["Asana API", "Trello API", "NexoraX Library"],
    knowledgeSources: ["Automation efficiency matrices"],
    exampleOutput: "“Roadmap compiled: 3 major manual invoice processes highlighted for immediate template swap.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "12 Hours / Week"
  },
  {
    id: "quality-review",
    name: "Quality Review Agent",
    role: "Output Integrity Checker",
    category: "Operations",
    bestFor: "Grammar verification, draft checklists, and brand alignment scores",
    tasks: [
      "Check grammar",
      "Check brand voice",
      "Check accuracy",
      "Check CTA",
      "Flag sensitive content",
      "Score quality",
      "Suggest edits",
      "Send to approval"
    ],
    setupTime: "3 Minutes",
    automationLevel: 94,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Checks draft text or outputs against strict brand specifications and flags corrections.",
    toolsConnected: ["NexoraX Drive", "Grammarly Business API"],
    knowledgeSources: ["NexoraX Brand styling guidelines v2"],
    exampleOutput: "“Integrity audit complete: Draft scored 94/100. Minor edits marked for invoice CTA.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "10 Hours / Week"
  },
  {
    id: "risk-escalation",
    name: "Risk Escalation Agent",
    role: "Sentry Audit Buffer",
    category: "Legal & Compliance Support",
    bestFor: "Highlighting high-risk drafts, financial claims verification, and administrative override logs",
    tasks: [
      "Detect sensitive topic",
      "Detect financial claim",
      "Detect legal language",
      "Detect angry customer",
      "Detect missing approval",
      "Escalate to admin",
      "Create risk summary",
      "Pause automation"
    ],
    setupTime: "3 Minutes",
    automationLevel: 96,
    humanApprovalRequired: true,
    status: "Premium Template",
    description: "Senses financial claims, legal wordings, or client complaints and locks active engines.",
    toolsConnected: ["Sentry Logger", "Slack Alert", "Chronos System"],
    knowledgeSources: ["NexoraX SLA & Gavel protocols"],
    exampleOutput: "“Risk alert logged: Financial claim identified inside outbound email. Sending paused for approval.”",
    riskLevel: "HIGH",
    recommendedPlan: "Corporate Alpha",
    timeSaved: "15 Hours / Week",
    isFeatured: true
  },
  {
    id: "internal-knowledge",
    name: "Internal Knowledge Agent",
    role: "FAQ Scribe Drone",
    category: "HR & Client Resource Workflow",
    bestFor: "Searching SOP sheets, answering policy checks, and identifying old versions",
    tasks: [
      "Search knowledge base",
      "Summarise policy",
      "Answer team questions",
      "Suggest document",
      "Flag outdated content",
      "Create knowledge update",
      "Track common questions",
      "Escalate missing info"
    ],
    setupTime: "4 Minutes",
    automationLevel: 92,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Crawls approved PDF guides to answer staff questions and track missing documentation slots.",
    toolsConnected: ["Confluence API", "Notion Base", "ElasticSearch Core"],
    knowledgeSources: ["Internal corporate SOP database"],
    exampleOutput: "“Audit answered: Standard compliance application step outline compiled from current sponsorship guideline files.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "12 Hours / Week"
  },
  {
    id: "campaign-planner",
    name: "Campaign Planner Agent",
    role: "Campaign Strategy Drone",
    category: "Marketing & Content",
    bestFor: "Coordinating multi-channel campaigns, schedule mapping, and outline briefs",
    tasks: [
      "Create campaign plan",
      "Generate content ideas",
      "Assign platforms",
      "Create timeline",
      "Draft messaging",
      "Suggest budget placeholder",
      "Create approval list",
      "Send to scheduler"
    ],
    setupTime: "5 Minutes",
    automationLevel: 82,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Generates multi-channel content ideas, schedules media assets, and sets drafts timeline.",
    toolsConnected: ["Slack Collaborate", "Trello Boards", "Loom API"],
    knowledgeSources: ["NexoraX Marketing playbooks"],
    exampleOutput: "“Strategy sheet ready: 12 assets specified for Resource resource launch campaign.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "14 Hours / Week"
  },
  {
    id: "ad-copy-agent",
    name: "Ad Copy Agent",
    role: "Conversion Scribe Node",
    category: "Marketing & Content",
    bestFor: "Generating conversion titles, feature callouts, and A/B test variations",
    tasks: [
      "Generate headlines",
      "Create ad text",
      "Create CTA variants",
      "Create A/B tests",
      "Match platform style",
      "Score conversion strength",
      "Prepare campaign copy",
      "Send to approval"
    ],
    setupTime: "3 Minutes",
    automationLevel: 88,
    humanApprovalRequired: true,
    status: "Ready to Deploy",
    description: "Synthesizes advertising titles, hooks, CTAs, and matches copy parameters to Google/META platforms.",
    toolsConnected: ["Facebook Ads API", "LinkedIn Partner Hub", "Google Ads Editor"],
    knowledgeSources: ["NexoraX high-conversion text pools"],
    exampleOutput: "“Ad copy draft ready: 3 Headline variations compiled with [ESCALATION_CONTACT] info.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "9 Hours / Week"
  },
  {
    id: "product-launch",
    name: "Product Launch Agent",
    role: "Release Coordinator Drone",
    category: "Operations",
    bestFor: "Coordinating product steps, release descriptions, and email marketing briefs",
    tasks: [
      "Create launch checklist",
      "Draft announcement",
      "Generate promo script",
      "Create social posts",
      "Prepare email campaign",
      "Create landing page copy",
      "Schedule launch tasks",
      "Track performance"
    ],
    setupTime: "5 Minutes",
    automationLevel: 85,
    humanApprovalRequired: true,
    status: "Approval Recommended",
    description: "Maintains product launch checklists and outputs promotion copies and blog announcements.",
    toolsConnected: ["GitHub Releases", "ProductHunt API", "ActiveCampaign"],
    knowledgeSources: ["Product Release Standards Protocol"],
    exampleOutput: "“Launch checklists compiled. Outbound announcement copies queued for Director review.”",
    riskLevel: "LOW",
    recommendedPlan: "Enterprise Pro",
    timeSaved: "16 Hours / Week"
  },
  {
    id: "support-knowledge",
    name: "Support Knowledge Base Agent",
    role: "Knowledge Base Scriptor",
    category: "Customer Support",
    bestFor: "Converting tickets into FAQs, drafting guides, and article updates",
    tasks: [
      "Convert support questions into FAQ",
      "Draft help article",
      "Suggest categories",
      "Update guide content",
      "Flag outdated answers",
      "Create tutorial script",
      "Link related guide",
      "Send for review"
    ],
    setupTime: "4 Minutes",
    automationLevel: 90,
    humanApprovalRequired: true,
    status: "Ready to Deploy",
    description: "Scans compiled ticket logs to form standard FAQs and update support knowledge bases.",
    toolsConnected: ["HelpScout API", "ReadMe API", "Notion Base"],
    knowledgeSources: ["Customer refund policies and SOPs"],
    exampleOutput: "“FAQ upgrade complete: 5 recurring ticket topics rewritten into step-by-step guides.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Elite",
    timeSaved: "12 Hours / Week"
  },
  {
    id: "admin-task",
    name: "Admin Task Agent",
    role: "Checklist General Scribe",
    category: "Executive & Admin",
    bestFor: "Coordinating task pipelines, reminders tracking, and administrative updates",
    tasks: [
      "Create checklist",
      "Draft reminder",
      "Summarise request",
      "Assign owner",
      "Track status",
      "Prepare admin report",
      "Escalate overdue task",
      "Close completed task"
    ],
    setupTime: "2 Minutes",
    automationLevel: 92,
    humanApprovalRequired: false,
    status: "Ready to Deploy",
    description: "Handles general workspace tasks checklists and triggers email updates on due dates.",
    toolsConnected: ["NexoraX Task Matrix", "Gmail Outbox API"],
    knowledgeSources: ["General task SLA manuals"],
    exampleOutput: "“Admin pipeline updated: 4 routine scheduling reminders generated.”",
    riskLevel: "LOW",
    recommendedPlan: "NexoraX Basic",
    timeSaved: "8 Hours / Week"
  }
];

export const TASK_FLOWS: TaskFlow[] = [
  {
    id: "tf-1",
    title: "1. New Lead Follow-Up",
    trigger: "New inbound form submit",
    agentAssigned: "Lead Qualification Agent",
    steps: [
      "Score incoming lead against target BANT rules",
      "Check budget values over Resource boundaries",
      "Create deal records in Hubspot CRM",
      "Draft first response using designated brand style",
      "Schedule follow-up check next Tuesday"
    ],
    approvalGate: "Human validation before outbox dispatch",
    output: "Qualified CRM lead profile & reply draft",
    estimatedTimeSaved: "3.5 Hours / Day"
  },
  {
    id: "tf-2",
    title: "2. Customer Complaint Escalation",
    trigger: "Angry customer sentiment detected",
    agentAssigned: "Customer Support Assistant",
    steps: [
      "Read ticket description to detect rage words",
      "Log priority score in support layout",
      "Prepare friendly diagnostic copy",
      "Route ticket to supervisor contact",
      "Flag account in Stripe billing base"
    ],
    approvalGate: "Strict director check before dispatch",
    output: "Priority escalation record, system lock logs",
    estimatedTimeSaved: "2.0 Hours / Day"
  },
  {
    id: "tf-3",
    title: "3. Daily Email Summary",
    trigger: "Scheduled Cron: Everyday 08:00 AM",
    agentAssigned: "Email Management Agent",
    steps: [
      "Scan all unread emails in inbox",
      "Filter newsletters and duplicate spam",
      "Identify open requests from clients",
      "Outline prioritized tasks task boards",
      "Draft concise overview briefing text"
    ],
    approvalGate: "Advisory briefing layout only",
    output: "Consolidated morning briefing summary list",
    estimatedTimeSaved: "1.5 Hours / Day"
  },
  {
    id: "tf-4",
    title: "4. Weekly Performance Report",
    trigger: "Scheduled Cron: Friday 05:00 PM",
    agentAssigned: "Data Reporting Agent",
    steps: [
      "Fetch weekly conversion and CPC metrics",
      "Analyze average response times across queues",
      "Compare costs with weekly budget models",
      "Generate Recharts visual graphs blocks",
      "Create detailed executive update PDF draft"
    ],
    approvalGate: "Director checkout required",
    output: "Integrity report PDF and metrics dashboard",
    estimatedTimeSaved: "4.0 Hours / Week"
  },
  {
    id: "tf-5",
    title: "5. Social Post Creation",
    trigger: "New content request",
    agentAssigned: "Social Media Manager",
    steps: [
      "Research trending technical themes on Reddit",
      "Write multi-platform caption packs",
      "Align syntax with Space Grotesk layout style",
      "Incorporate required Stripe Payment Link",
      "Add hashtags and target keywords"
    ],
    approvalGate: "PR Specialist confirmation",
    output: "Formatted platform updates package",
    estimatedTimeSaved: "3.0 Hours / Day"
  },
  {
    id: "tf-6",
    title: "6. Blog Draft Creation",
    trigger: "Scheduled outline compile",
    agentAssigned: "Blog Writer Agent",
    steps: [
      "Vet SEO titles against indexing thresholds",
      "Compile comprehensive outline files",
      "Synthesize complete written section drafts",
      "Check keyword densities recursively",
      "Prepare associated LinkedIn snippets"
    ],
    approvalGate: "Chief Editor approval mandatory",
    output: "Ready to publish markdown blog post text",
    estimatedTimeSaved: "5.0 Hours / Post"
  },
  {
    id: "tf-7",
    title: "7. Product Research Comparison",
    trigger: "Buying request submitted",
    agentAssigned: "Product Search Agent",
    steps: [
      "Crawl target merchant prices and models",
      "Check for user review rankings",
      "Prepare extensive cost-saving analysis",
      "Generate shortlist tables",
      "Recommend cheapest SLA fit option"
    ],
    approvalGate: "Auto-runs, no direct gate needed",
    output: "Vetted suppliers comparative spreadsheet",
    estimatedTimeSaved: "2.5 Hours / Request"
  },
  {
    id: "tf-8",
    title: "8. Supplier Shortlist",
    trigger: "Procurement event logged",
    agentAssigned: "Supplier Research Agent",
    steps: [
      "Audit international logistics limits",
      "Establish direct supplier catalogs",
      "Assess shipping and custom clearance delays",
      "Verify factory licenses against rules",
      "Issue brief containing top 3 recommendations"
    ],
    approvalGate: "Purchasing director verification",
    output: "Shortlist brief, logistics validation reports",
    estimatedTimeSaved: "6.0 Hours / Supplier Vetting"
  },
  {
    id: "tf-9",
    title: "9. Client Onboarding Checklist",
    trigger: "New Client billing verification",
    agentAssigned: "Onboarding Agent",
    steps: [
      "Send onboarding checklist questionnaire",
      "Collect brand colors and business records",
      "Set up workspace folders",
      "Instruct customer on connection inputs",
      "Log completion checklist"
    ],
    approvalGate: "Auto-runs on setup checks",
    output: "Onboarding tracking board and shared folders",
    estimatedTimeSaved: "3.0 Hours / Client"
  },
  {
    id: "tf-10",
    title: "10. Meeting Notes to Action Plan",
    trigger: "Audio transcriber finish",
    agentAssigned: "Meeting Notes Agent",
    steps: [
      "Scan transcript for decision triggers",
      "Format clear task items lists",
      "Assign project deadlines and owners",
      "Draft recap emails to all attendees",
      "Mirror records to archive database"
    ],
    approvalGate: "Meeting host approval first",
    output: "Action schedule, client digest drafting",
    estimatedTimeSaved: "2.0 Hours / Meeting"
  },
  {
    id: "tf-11",
    title: "11. Invoice Reminder Draft",
    trigger: "Billing overdue flag in Ledger",
    agentAssigned: "Invoice & Billing Assistant",
    steps: [
      "Extract client company email from directory",
      "Cross-check unpaid invoices records",
      "Compile polite reminder drafts with links",
      "Format billing payment table",
      "Alert accounting team on Slack"
    ],
    approvalGate: "Finance manager approve before email outbox",
    output: "Draft email, updated ledger tracker logs",
    estimatedTimeSaved: "1.0 Hour / Day"
  },
  {
    id: "tf-12",
    title: "12. Campaign Approval Workflow",
    trigger: "Creative bundle compiled",
    agentAssigned: "Campaign Planner Agent",
    steps: [
      "Validate ad assets resolutions",
      "Audit pricing claims against Resource definitions",
      "Verify target link redirection safety",
      "Compile overview slides",
      "Send to executive channel for final signoff"
    ],
    approvalGate: "Director absolute verification",
    output: "Approved marketing asset manifest package",
    estimatedTimeSaved: "4.5 Hours / Campaign"
  },
  {
    id: "tf-13",
    title: "13. Website Update Announcement",
    trigger: "Linter success & code compile push",
    agentAssigned: "Website Update Agent",
    steps: [
      "Extract commit logs and summaries index",
      "Compare file directory changes",
      "Format elegant release changelog texts",
      "Draft promo captions",
      "Send alert to operations Slack channels"
    ],
    approvalGate: "Lead developer confirmation",
    output: "Release report, formatted social change-log",
    estimatedTimeSaved: "1.5 Hours / Release"
  },
  {
    id: "tf-14",
    title: "14. New Service Promo Draft",
    trigger: "New Service release in repository",
    agentAssigned: "Ad Copy Agent",
    steps: [
      "Assess target user benefits and offer",
      "Generate 3 variant hook lines",
      "Structure persuasive CTAs",
      "Build A/B test outlines for Meta Ads",
      "Prepare email broadcast drafts"
    ],
    approvalGate: "Marketing review",
    output: "Promotional copywriting variations deck",
    estimatedTimeSaved: "3.5 Hours / Promo Set"
  },
  {
    id: "tf-15",
    title: "15. Support Ticket Triage",
    trigger: "Inbound email to support@ address",
    agentAssigned: "Customer Support Assistant",
    steps: [
      "Inspect email keywords to trace subject",
      "Match sender with active CRM user slots",
      "Apply priority category labels",
      "Form standard help-docs link recommendations",
      "Insert draft reply to ticketing queue"
    ],
    approvalGate: "Review first 50 replies manually",
    output: "Categorised tickets, diagnostic answers drafts",
    estimatedTimeSaved: "2.5 Hours / Day"
  },
  {
    id: "tf-16",
    title: "16. CRM Update Flow",
    trigger: "Discovery interview complete",
    agentAssigned: "CRM Update Agent",
    steps: [
      "Summarize interview notes transcript",
      "Update budget status dealing logs",
      "Schedule core team briefing task",
      "Notify Vanguard closer node on WhatsApp",
      "Cross-check contact profile metadata parameters"
    ],
    approvalGate: "Liaison validation",
    output: "Updated HubSpot record and meeting log logs",
    estimatedTimeSaved: "1.0 Hour / Deal"
  },
  {
    id: "tf-17",
    title: "17. Demo Booking Flow",
    trigger: "Lead qualifies above score 90/100",
    agentAssigned: "Booking Assistant Agent",
    steps: [
      "Check calendar slots against availability rules",
      "Send custom Cal.com booking link to lead",
      "Track when date selection is completed",
      "Generate Zoom meeting link",
      "Alert sales operator on Slack"
    ],
    approvalGate: "Autonomous scheduling enabled",
    output: "Confirmed calendar appointment, Slack alert",
    estimatedTimeSaved: "2.0 Hours / Booking"
  },
  {
    id: "tf-18",
    title: "18. Review Request Flow",
    trigger: "Successful payout or service delivery",
    agentAssigned: "Review & Reputation Agent",
    steps: [
      "Select contact email from client databases",
      "Send satisfaction survey link",
      "Check returned ratings and stars counts",
      "Send GBP review invitation link",
      "Draft response for GBP dashboard"
    ],
    approvalGate: "GBP response requires approval",
    output: "Feedback tracking sheets, review request email",
    estimatedTimeSaved: "1.5 Hours / Day"
  },
  {
    id: "tf-19",
    title: "19. Churn Risk Alert",
    trigger: "Account inactivity past 14 days",
    agentAssigned: "Client Success Agent",
    steps: [
      "Audit active logins inside cloud databases",
      "Identify client key personnel contacts",
      "Compile custom value report summary",
      "Draft personalized check-in outreach",
      "Alert account executive on Slack dashboard"
    ],
    approvalGate: "Manual check before outbox message",
    output: "Priority alert profile, check-in email draft",
    estimatedTimeSaved: "3.0 Hours / Week"
  },
  {
    id: "tf-20",
    title: "20. Monthly Executive Brief",
    trigger: "Scheduled Cron: 1st of month",
    agentAssigned: "Executive Assistant Agent",
    steps: [
      "Consolidate revenue spreadsheets, costs, and tax audits",
      "Review milestones achievement metrics across projects",
      "Identify unresolved issues",
      "List priority items for the coming month",
      "Draft elegant pdf briefing text for Director"
    ],
    approvalGate: "Review draft, final output readable only",
    output: "Consolidated PDF Briefing Document",
    estimatedTimeSaved: "6.0 Hours / Month"
  }
];
